import { ApiResponseData, skipioAxios } from ".";

type DeliverableDayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

// Merging of `feature_flags` on `skipio_plans` and `extra_features` on `users`
export type FeatureFlag =
  | "alerts"
  | "asset_library"
  | "automations"
  | "beta__appointments"
  | "beta__custom_user_fields"
  | "beta__events"
  | "beta__legacy__flows"
  | "beta__legacy__flows--create"
  | "beta__solution_center"
  | "call_forwarding"
  | "campaign_package_creation"
  | "community__cms"
  | "community__cms_targeting"
  | "dashboard"
  | "engagements"
  | "event_campaigns"
  | "events"
  | "flows"
  | "integrations__clickfunnels"
  | "integrations__mindbody"
  | "integrations__zenplanner"
  | "integrations__hubspot"
  | "integrations__dynamics_crm"
  | "integrations__infusionsoft_crm"
  | "integrations__intouch"
  | "integrations__base_crm"
  | "integrations__zoho_crm"
  | "integrations__clubready"
  | "integrations__zapier"
  | "integrations__acuity_v2"
  | "integrations__calendly_v3"
  | "integrations__clickfunnels_v2"
  | "integrations__gsheets"
  | "invite_token"
  | "on_site_user_creation"
  | "last_in_out"
  | "mary_kay_my_customer"
  | "mary_kay_my_business"
  | "mary_kay_freemium"
  | "message_library"
  | "outbound_calling"
  | "paused"
  | "reports__bounce"
  | "responder"
  | "sales"
  | "sentiment"
  | "smart_campaigns"
  | "support__chat"
  | "support__email"
  | "support__phone"
  | "support__video"
  | "system_administration"
  | "teams"
  | "voice_broadcasts"
  | "voicemail_campaigns"
  | "web2txt";

export type User = {
  id: string;
  accountOwner: boolean;
  agencyOwner: boolean;
  avatarImageUrl: string;
  birthdayGreetingBody: string;
  birthdayGreetingMediaMimeTypes: string[];
  birthdayGreetingMediaUrls: string[];
  callForwardPhoneNumber: string;
  campaignsCount: number;
  contactGroupsCount: number;
  deletedAt: number;
  deliverableHours: {
    [key in DeliverableDayOfWeek]: {
      enabled: boolean;
      hours: [number, number];
    };
  };
  email: string;
  enabledFeatures: FeatureFlag[];
  firstName: string;
  lastName: string;
  logoImageUrl: string;
  maxCharacters: number;
  maxContacts: number;
  monthlyMaxSmsMsgSend: number;
  phoneMobile: string;
  standardTimeZone: string;
  timeZone: string;
  providerPhoneNumbers: {
    id: string;
    phoneNumber: string;
    isShortCode: boolean;
    label: string;
    avatarUrl: string;
  }[];
  isTrueUser: boolean;
  account: {
    id: string;
    name: string;
    maximumUsers: number;
    maximumContacts: number;
    maximumMonthlySmsMessages: number;
    teamsEnabled: boolean;
    skipioPlanId: string;
    contactsCount: number;
    deletedContactsCount: number;
    isEnabled: boolean;
    disabledReason: string;
    hasStripeCustomerId: boolean;
    demoExpiresAt: string;
    isShortCode: boolean;
    billingType: string;
    registrationStatus: string;
    ignoreRegistrationStatus: boolean;
    addons: {
      quantity: number;
      skipioPlanId: string;
      name: string;
      price: number;
      addonType: "user" | "contact";
      additionalCount: number;
    }[];
    belongsToAgency: {
      id: string;
      name: string;
      accountOwnerUserIds: string[];
    } | null;
    managesAgency: {
      id: string;
      name: string;
      accountOwnerUserIds: string[];
    } | null;
  };
  skipioPlan: {
    monthlySmsMessagesSent: number;
    dailyVoicemailMessagesSent: number;
    monthlyVoicemailMessagesSent: number;
    dripCampaignsCount: number;
    eventCampaignsCount: number;
    reminderCampaignsCount: number;
    automationsCount: number;
    accountContactsCount: number;
    maxIntegrations: number;
  };
  customFields: {
    label: string;
    name: string;
  }[];
  teams: {
    id: string;
    name: string;
    isLeader: boolean;
  }[];
};

type BadgeCounts = {
  unreadCount: number;
};

export const fetchUser = async () => {
  const res = await skipioAxios({
    method: "GET",
    url: "users/me",
  });

  return res.data as ApiResponseData<User>;
};

export const fetchBadgeCounts = async () => {
  const res = await skipioAxios({
    method: "GET",
    url: "users/me/badge_counts/",
  });

  return res.data as ApiResponseData<BadgeCounts>;
};
